import { LabSubscribeButton, LabSubscribersOnlyCard } from '../lab/LabSubscribe.component'

import { AdjustInstructions } from './AdjustToolsPanel.component'
import { BlurControls } from './tools/BlurControls.component'
import { EditionType } from '../photoStudioTypes'
import { FC } from 'react'
import { LabPanelShell } from '../lab/LabPanelPrimitives.component'
import { NonSubscriberAdjustUpsell } from '../AdjustPanel/NonSubscriberAdjustUpsell.component'
import { Stack } from '@mui/material'
import { useAdjustEditor } from './AdjustEditor.context'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

interface BlurPanelProps {
  /** Inside the Blur section of Combine tools: no own padding, scroll, card or Subscribe (the view has them) */
  embedded?: boolean
}

/**
 * Blur on its own (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT): the in-house editor's Blur tool, Brush or Area, under a
 * callout like Adjust's. Same editor and document as Adjust, so its edits apply as they are painted and are saved with
 * Adjust's, when the editor is left. Non-subscribers: locked on the Lab engine, today's Adjust upsell without it.
 */
export const BlurPanel: FC<BlurPanelProps> = ({ embedded = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const { isBlurTool, isProcessingImage, isOriginalVisualType, isLabSdkEngine } = useClientPhotoStudio()
  const { base } = useAdjustEditor()

  const isUpsell = !hasWorkspaceActiveSubscription && !isLabSdkEngine
  const isLocked = !hasWorkspaceActiveSubscription && isLabSdkEngine
  const isDisabled = isLocked || isProcessingImage || isOriginalVisualType

  const body = (
    <Stack gap="1.2rem">

      {/* Adjust's sentence: changes apply as they are made, no Generate, undo with the arrows */}
      <AdjustInstructions title={t('blur_instructions.title')} />

      {/* Combine tools: expanding the section puts Blur on the canvas — until it is there, nothing to paint on */}
      <BlurControls disabled={isDisabled || !base || !isBlurTool} />

    </Stack>
  )

  return (
    <>

      {isUpsell && <NonSubscriberAdjustUpsell />}

      {!isUpsell && embedded && body}

      {!isUpsell && !embedded && isLocked && (
        <LabPanelShell footer={<LabSubscribeButton editionType={EditionType.ADJUST} />}>

          <LabSubscribersOnlyCard margin="0 0 2.4rem" />

          {body}

        </LabPanelShell>
      )}

      {!isUpsell && !embedded && !isLocked && (
        // The column adds no padding (see AdjustToolsPanel): the edge it clips at stays clear of a slider thumb at its minimum
        <Stack sx={{ width: '100%', height: '100%', minHeight: 0, padding: '2.4rem', boxSizing: 'border-box', overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' }}>
          {body}
        </Stack>
      )}

    </>
  )
}
